/* ── PAG constants ───────────────────────────────────────────────────────
   Values shared across the Privilege Access Governance screens. Anything a
   single screen owns stays with that screen; this file is for what more than
   one of them needs, or what a reviewer would want to find in one place. */

import { MAX_PAGE_SIZE } from "../../../../utils/formatters";

/** Only the on-prem AD source is wired today; the endpoints take its id. */
export const SOURCE_SYSTEM_ID = 1;

/** The risk board reads every account, in pages of this size. */
export const RISK_BOARD_PAGE_SIZE = MAX_PAGE_SIZE;

/* ── Colour ─────────────────────────────────────────────────────────── */

/** One hue per account family, so the same kind of account reads the same
 *  colour in every card and chart. */
export const ACCOUNT_COLORS = {
  domain: { hex: "#3b82f6", text: "text-blue-500" },
  enterprise: { hex: "#a855f7", text: "text-purple-500" },
  schema: { hex: "#14b8a6", text: "text-teal-500" },
  server: { hex: "#22c55e", text: "text-green-500" },
  sudo: { hex: "#eab308", text: "text-yellow-500" },
  service: { hex: "#f97316", text: "text-orange-500" },
  local: { hex: "#ec4899", text: "text-pink-500" },
  cloud: { hex: "#06b6d4", text: "text-cyan-500" },
} as const;

/** Group names come from the directory, so the By Type chart takes hues off
 *  the palette in order rather than from a name-to-colour map. */
export const GROUP_CHART_COLORS = [
  ACCOUNT_COLORS.domain.hex,
  ACCOUNT_COLORS.enterprise.hex,
  ACCOUNT_COLORS.schema.hex,
  ACCOUNT_COLORS.server.hex,
  ACCOUNT_COLORS.sudo.hex,
  ACCOUNT_COLORS.service.hex,
  ACCOUNT_COLORS.local.hex,
  ACCOUNT_COLORS.cloud.hex,
];

/** Severity scale, including the API's uncategorised bucket. */
export const RISK_COLORS: Record<string, string> = {
  CRITICAL: "#EF4444",
  HIGH: "#F97316",
  MEDIUM: "#FFB84D",
  LOW: "#00B8D4",
  UNCATEGORISED: "#94A3B8",
};

/* ── Status pills ───────────────────────────────────────────────────── */

export const PILL_BASE =
  "inline-flex w-full max-w-[150px] items-center justify-center whitespace-nowrap rounded-full px-2 py-1 text-xs font-bold ring-1";

export const PILL_TONES = {
  red: "bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400 ring-red-200 dark:ring-red-500/20",
  orange:
    "bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-400 ring-orange-200 dark:ring-orange-500/20",
  amber:
    "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 ring-amber-200 dark:ring-amber-500/20",
  cyan: "bg-cyan-50 dark:bg-cyan-500/10 text-cyan-700 dark:text-cyan-400 ring-cyan-200 dark:ring-cyan-500/20",
  emerald:
    "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 ring-emerald-200 dark:ring-emerald-500/20",
  blue: "bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 ring-blue-200 dark:ring-blue-500/20",
  purple:
    "bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-400 ring-purple-200 dark:ring-purple-500/20",
  slate: "bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-white/60 ring-slate-200 dark:ring-white/10",
} as const;

export type PillTone = keyof typeof PILL_TONES;

/** Review workflow states, as the API spells them. */
export const REVIEW_TONES: Record<string, PillTone> = {
  "Revoke Requested": "purple",
  "Pending Review": "blue",
  Reviewed: "emerald",
  Revoked: "red",
  Pending: "slate",
  Overdue: "red",
};

/* ── Account list filters ───────────────────────────────────────────────
   The list endpoint filters on the status text itself, so these are the
   values it accepts - not display labels. Statuses the API returns that
   are missing here still show up: the dropdowns union these with whatever
   the loaded rows carry. */

export const PAM_STATUS_FILTERS = ["Vaulted", "Not Vaulted"];

export const REVIEW_STATUS_FILTERS = [
  "Not Reviewed",
  "Pending Review",
  "Continue",
  "Revoke Requested",
  "Review Rejected",
  "Revoked",
];

/* Statuses the API still returns but the Review filter does not offer.
   Needed as its own list because the dropdown unions these constants with
   whatever the loaded rows carry -- dropping a value from the list above
   is not enough on its own, it would reappear the moment a row had it. */
export const REVIEW_STATUS_FILTERS_HIDDEN = ["Unassigned"];

/** The two statuses that mean a request is open and awaiting a decision:
 *  one raised to re-confirm access, one raised to take it away. */
export const PENDING_REVIEW_STATUS = "Pending Review";
export const REVOKE_REQUESTED_STATUS = "Revoke Requested";

/** How a status is worded in the UI, where that differs from the value the
 *  API filters on. Renaming the value itself would break the request. */
export const REVIEW_STATUS_LABELS: Record<string, string> = {
  "Review Rejected": "Rejected",
};

/** The status as it should read on screen. */
export const reviewStatusLabel = (status: string) => REVIEW_STATUS_LABELS[status] ?? status;

/* ── Risk board ─────────────────────────────────────────────────────── */

export type RiskLevel = "CRITICAL" | "HIGH" | "MEDIUM" | "LOW";
/** Board column for accounts nobody has rated yet. */
export type BoardColumn = RiskLevel | "UNASSIGNED";

export const RISK_META: Record<
  BoardColumn,
  { label: string; chip: string; dot: string; ring: string }
> = {
  CRITICAL: {
    label: "Critical",
    chip: "bg-red-500/10 text-red-500",
    dot: "bg-red-500",
    ring: "border-red-500/30",
  },
  HIGH: {
    label: "High",
    chip: "bg-orange-500/10 text-orange-500",
    dot: "bg-orange-500",
    ring: "border-orange-500/30",
  },
  MEDIUM: {
    label: "Medium",
    chip: "bg-amber-500/10 text-amber-500",
    dot: "bg-amber-500",
    ring: "border-amber-500/30",
  },
  LOW: {
    label: "Low",
    chip: "bg-emerald-500/10 text-emerald-500",
    dot: "bg-emerald-500",
    ring: "border-emerald-500/30",
  },
  UNASSIGNED: {
    label: "Unassigned",
    chip: "bg-muted/40 text-muted-foreground",
    dot: "bg-muted-foreground",
    ring: "border-border",
  },
};

/** Unassigned leads, so unclassified accounts are what you see first. */
export const BOARD_COLUMNS: BoardColumn[] = ["UNASSIGNED", "CRITICAL", "HIGH", "MEDIUM", "LOW"];

/* ── Standard Rules ─────────────────────────────────────────────────────
   The built-in classification behind Auto Configuration: break-glass and
   UID 0 style accounts are Critical, domain-wide or shared administrative
   rights are High, any other privileged group membership is Medium, and
   everything else is Low. */
export const BREAK_GLASS_NAMES = [
  "administrator",
  "root",
  "krbtgt",
  "breakglass",
  "break-glass",
  "emergency",
];

export const DOMAIN_WIDE_GROUPS = [
  "domain admins",
  "enterprise admins",
  "administrators",
  "schema admins",
];
